import {QueryClient, useQueryClient} from '@tanstack/react-query'
import {useCallback} from 'react'
import {APIConfigType, ContextualPrefetchType} from 'interfaces'
import {prefetchConfig} from './prefetch.config'
import {reactQueryFetchFunctionMap, warmUpFunctionMap} from './prefetch.queryfn'
import {checkPermission, usePermissions} from 'rbac'
import Logger from 'services/Logger'

const reportContextualPrefetchError = (
  prefetchSource: string,
  error: unknown,
): void => {
  const normalizedError =
    error instanceof Error ? error : new Error(String(error))

  Logger.LogError(
    new Error(
      `Contextual prefetch failed for ${prefetchSource}: ${normalizedError.message}`,
    ),
  )
}

const runContextualPrefetch = (
  prefetchSource: string,
  fetcher: () => Promise<void> | void,
): void => {
  void Promise.resolve()
    .then(fetcher)
    .catch(error => reportContextualPrefetchError(prefetchSource, error))
}

type UseContextualPrefetchResult = {
  prefetch: (prefetchParams: ContextualPrefetchType) => void
}

/**
 * @description A Hook to prefetch data on respective contexts
 * @returns prefetch function
 */
const useContextualPrefetch = (): UseContextualPrefetchResult => {
  const queryClient = useQueryClient()
  const {permissions: userPermissions} = usePermissions()
  const prefetch = useCallback(
    ({
      route,
      context,
      payload,
      skipQueryFnNames = [],
    }: ContextualPrefetchType) => {
      const contextualConfig =
        prefetchConfig.contextual[
          route as keyof typeof prefetchConfig.contextual
        ]
      const config = contextualConfig?.[
        context as keyof typeof contextualConfig
      ] as unknown as APIConfigType[] | undefined

      if (!config) return

      const payloadMap = payload ?? {}
      const skip = new Set<string>(skipQueryFnNames as string[])
      const reactQueryFetchers =
        reactQueryFetchFunctionMap as unknown as Record<
          string,
          (
            queryClient: QueryClient,
            queryKey?: unknown,
            payload?: unknown,
          ) => Promise<void> | void
        >
      const warmUpFetchers = warmUpFunctionMap as unknown as Record<
        string,
        (payload?: unknown) => Promise<void> | void
      >

      config.forEach((apiConfig: APIConfigType) => {
        const {isReactQuery, reqPermissions} = apiConfig

        const hasPermission = checkPermission(reqPermissions, userPermissions)
        if (!hasPermission) return

        if (isReactQuery) {
          const {queryKey, queryFnName} = apiConfig
          if (skip.has(queryFnName)) return
          const data = queryClient.getQueryData(queryKey)

          if (!data) {
            const fetchFunction = reactQueryFetchers[queryFnName]
            if (!fetchFunction) return

            runContextualPrefetch(queryFnName, () =>
              fetchFunction(queryClient, queryKey, payloadMap[queryFnName]),
            )
          }
        } else {
          const {id} = apiConfig
          if (skip.has(id)) return
          const warmUpFunction = warmUpFetchers[id]
          if (!warmUpFunction) return

          runContextualPrefetch(id, () => warmUpFunction(payloadMap[id]))
        }
      })
    },
    [queryClient, userPermissions],
  )

  return {prefetch}
}

export default useContextualPrefetch
